import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, ReceiptText, X, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, apiError } from "@/lib/api";
import { fmtTime } from "@/lib/slots";

export default function ManageSession({ bookingId, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [items, setItems] = useState([]);
  const [food, setFood] = useState({ item_id: "", qty: 1 });
  const [charge, setCharge] = useState({ name: "", amount: "", qty: 1, note: "" });
  const [disc, setDisc] = useState({ type: "flat", value: "", note: "" });
  const [busy, setBusy] = useState(false);
  const [method, setMethod] = useState("cash");
  const [cashReceived, setCashReceived] = useState("");

  const load = () => api.get(`/admin/bookings/${bookingId}/session`).then((r) => { setData(r.data); setMethod(r.data.booking.payment_method || "cash"); });
  useEffect(() => { load(); api.get("/admin/items").then((r) => setItems(r.data.filter((i) => i.available))); /* eslint-disable-next-line */ }, [bookingId]);

  const refresh = (r) => { setData(r.data); onChanged && onChanged(); };
  const addFood = async () => { if (!food.item_id) return; try { refresh(await api.post(`/admin/bookings/${bookingId}/food`, { item_id: food.item_id, qty: +food.qty || 1 })); setFood({ item_id: "", qty: 1 }); } catch (e) { toast.error(apiError(e)); } };
  const addCharge = async () => { if (!charge.name.trim() || !charge.amount) return; try { refresh(await api.post(`/admin/bookings/${bookingId}/charge`, { name: charge.name, amount: +charge.amount, qty: +charge.qty || 1, note: charge.note })); setCharge({ name: "", amount: "", qty: 1, note: "" }); } catch (e) { toast.error(apiError(e)); } };
  const applyDisc = async () => { try { refresh(await api.post(`/admin/bookings/${bookingId}/discount`, { type: disc.type, value: +disc.value || 0, note: disc.note })); } catch (e) { toast.error(apiError(e)); } };
  const printThermal = (bill) => {
    const html = `<html><head><title>${bill.bill_no}</title><style>
      @page{size:80mm auto;margin:0}
      *{font-family:'Courier New',monospace}
      body{width:72mm;margin:0 auto;padding:6px 4px;color:#000;font-size:12px;line-height:1.4}
      .c{text-align:center}.b{font-weight:bold}.big{font-size:15px}
      .r{display:flex;justify-content:space-between}
      hr{border:none;border-top:1px dashed #000;margin:5px 0}
    </style></head><body>
      <div class="c b big">PLAY STATION</div>
      <div class="c b">GAMING CAFÉ</div>
      <div class="c">PLAY • EAT • CONNECT</div>
      <hr/>
      <div class="r"><span>Bill: ${bill.bill_no}</span><span>${(bill.created_at || "").slice(0, 10)}</span></div>
      <div>Customer: ${bill.customer_name || "-"}</div>
      <div>Mobile: ${bill.mobile || "-"}</div>
      <div>Station: ${bill.station_name} (${bill.setup_name})</div>
      <div>Time: ${fmtTime(bill.start_time)}-${fmtTime(bill.end_time)}</div>
      <hr/>
      <div class="b">Gaming</div>
      ${bill.gaming.map((g) => `<div>${g.desc}</div><div class="r"><span></span><span>Rs ${g.amount}</span></div>`).join("")}
      ${bill.food.length ? '<div class="b">Food & Drinks</div>' : ""}
      ${bill.food.map((f) => `<div class="r"><span>${f.name} x${f.qty}</span><span>Rs ${f.amount}</span></div>`).join("")}
      <hr/>
      <div class="r"><span>Subtotal</span><span>Rs ${bill.subtotal}</span></div>
      ${bill.discount > 0 ? `<div class="r"><span>Discount</span><span>-Rs ${bill.discount}</span></div>` : ""}
      <div class="r b big"><span>TOTAL</span><span>Rs ${bill.final_amount}</span></div>
      ${bill.advance_paid > 0 ? `<div class="r"><span>Advance Paid</span><span>Rs ${bill.advance_paid}</span></div>` : ""}
      <div class="r b"><span>${bill.remaining > 0 ? "REMAINING" : "PAID"}</span><span>Rs ${bill.remaining > 0 ? bill.remaining : bill.final_amount}</span></div>
      ${bill.payment_method === "cash" && bill.cash_received ? `<div class="r"><span>Cash Received</span><span>Rs ${bill.cash_received}</span></div><div class="r"><span>Change</span><span>Rs ${bill.change}</span></div>` : ""}
      <hr/>
      <div class="c">Payment: ${bill.payment_method.toUpperCase()} (${bill.payment_status.toUpperCase()})</div>
      <hr/>
      <div class="c b">THANK YOU!</div>
      <div class="c">GOOD GAMES BETTER PEOPLE</div>
    </body></html>`;
    const w = window.open("", "_blank", "width=380,height=640");
    if (!w) { toast.error("Allow pop-ups to print the bill"); return; }
    w.document.write(html); w.document.close(); w.focus();
    setTimeout(() => { w.print(); }, 400);
  };

  const endBill = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/bookings/${bookingId}/end-bill`, { payment_method: method, cash_received: method === "cash" ? (Number(cashReceived) || 0) : 0 });
      toast.success(`Billed · ${r.data.bill.bill_no}`);
      printThermal(r.data.bill);
      onChanged && onChanged(); onClose();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const b = data?.booking, bill = data?.bill;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#120A21] border-purple-500/30 text-slate-100 max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display">Manage Session</DialogTitle></DialogHeader>
        {!data ? <Loader2 className="animate-spin text-purple-400" /> : (
          <div className="space-y-4">
            <div className="glass rounded-xl p-3 text-sm">
              <p className="font-semibold">{b.customer_name} · {b.station_name}</p>
              <p className="text-slate-400">{b.setup_name} · {fmtTime(b.start_time)}–{fmtTime(b.end_time)}</p>
            </div>

            <div>
              <p className="text-xs uppercase tracking-wider text-purple-400 font-mono mb-2">Add Food & Drinks</p>
              <div className="flex gap-2">
                <select data-testid="ms-food-item" value={food.item_id} onChange={(e) => setFood({ ...food, item_id: e.target.value })} className="flex-1 h-10 rounded-xl bg-purple-950/40 border border-purple-500/30 px-2 text-sm"><option value="">Select item</option>{items.map((i) => <option key={i.id} value={i.id}>{i.name} · ₹{i.selling_price} ({i.stock})</option>)}</select>
                <Input data-testid="ms-food-qty" type="number" value={food.qty} onChange={(e) => setFood({ ...food, qty: e.target.value })} className="h-10 w-16 bg-purple-950/30 border-purple-500/30" />
                <Button data-testid="ms-food-add" onClick={addFood} className="h-10 bg-purple-600 hover:bg-purple-500"><Plus size={15} /></Button>
              </div>
            </div>

            <div>
              <p className="text-xs uppercase tracking-wider text-purple-400 font-mono mb-2">Add Custom Charge</p>
              <div className="flex gap-2 flex-wrap">
                <Input data-testid="ms-charge-name" value={charge.name} onChange={(e) => setCharge({ ...charge, name: e.target.value })} placeholder="e.g. Extra Controller" className="h-10 flex-1 min-w-[120px] bg-purple-950/30 border-purple-500/30" />
                <Input data-testid="ms-charge-amount" type="number" value={charge.amount} onChange={(e) => setCharge({ ...charge, amount: e.target.value })} placeholder="₹" className="h-10 w-20 bg-purple-950/30 border-purple-500/30" />
                <Input type="number" value={charge.qty} onChange={(e) => setCharge({ ...charge, qty: e.target.value })} className="h-10 w-14 bg-purple-950/30 border-purple-500/30" />
                <Button data-testid="ms-charge-add" onClick={addCharge} className="h-10 bg-purple-600 hover:bg-purple-500"><Plus size={15} /></Button>
              </div>
            </div>

            <div>
              <p className="text-xs uppercase tracking-wider text-purple-400 font-mono mb-2">Discount</p>
              <div className="flex gap-2">
                <select value={disc.type} onChange={(e) => setDisc({ ...disc, type: e.target.value })} className="h-10 rounded-xl bg-purple-950/40 border border-purple-500/30 px-2 text-sm"><option value="flat">₹ Flat</option><option value="percent">% Percent</option></select>
                <Input data-testid="ms-disc-value" type="number" value={disc.value} onChange={(e) => setDisc({ ...disc, value: e.target.value })} placeholder="Value" className="h-10 w-24 bg-purple-950/30 border-purple-500/30" />
                <Input value={disc.note} onChange={(e) => setDisc({ ...disc, note: e.target.value })} placeholder="Reason" className="h-10 flex-1 bg-purple-950/30 border-purple-500/30" />
                <Button data-testid="ms-disc-apply" onClick={applyDisc} className="h-10 bg-purple-600 hover:bg-purple-500">Apply</Button>
              </div>
            </div>

            <div className="glass rounded-xl p-3 text-sm space-y-1" data-testid="ms-bill">
              <div className="flex justify-between"><span className="text-slate-400">Gaming</span><span className="font-mono">₹{bill.gaming_amount}</span></div>
              {bill.items.map((i, x) => <div key={x} className="flex justify-between text-slate-300"><span>{i.name} × {i.qty}</span><span className="font-mono">₹{i.price * i.qty}</span></div>)}
              {bill.charges.map((c, x) => <div key={x} className="flex justify-between text-slate-300"><span>{c.name} × {c.qty || 1}</span><span className="font-mono">₹{c.amount * (c.qty || 1)}</span></div>)}
              <div className="flex justify-between border-t border-purple-500/10 pt-1"><span className="text-slate-400">Subtotal</span><span className="font-mono">₹{bill.subtotal}</span></div>
              {bill.discount > 0 && <div className="flex justify-between text-amber-400"><span>Discount{bill.discount_note ? ` (${bill.discount_note})` : ""}</span><span className="font-mono">-₹{bill.discount}</span></div>}
              <div className="flex justify-between font-bold text-emerald-400"><span>TOTAL</span><span className="font-mono" data-testid="ms-total">₹{bill.total}</span></div>
              <div className="flex justify-between text-slate-400"><span>Advance Paid</span><span className="font-mono">₹{bill.advance_paid}</span></div>
              <div className="flex justify-between text-amber-300"><span>Remaining</span><span className="font-mono" data-testid="ms-remaining">₹{bill.remaining}</span></div>
            </div>

            <div>
              <p className="text-xs uppercase tracking-wider text-purple-400 font-mono mb-2">Payment Method</p>
              <div className="flex gap-2 flex-wrap">
                {["cash", "upi", "payu"].map((m) => (
                  <button key={m} data-testid={`ms-pay-${m}`} onClick={() => setMethod(m)} className={`px-4 h-10 rounded-xl text-sm border uppercase ${method === m ? "bg-purple-600 border-purple-400" : "glass border-purple-500/30 text-slate-300"}`}>{m}</button>
                ))}
              </div>
              {method === "cash" && (
                <div className="mt-3">
                  <label className="text-xs text-purple-400 font-mono">Cash Received (₹)</label>
                  <Input data-testid="ms-cash-received" type="number" value={cashReceived} onChange={(e) => setCashReceived(e.target.value)} placeholder={`₹${bill.remaining}`} className="h-11 mt-1 bg-purple-950/30 border-purple-500/30 font-mono" />
                  {cashReceived !== "" && (Number(cashReceived) >= bill.remaining
                    ? <p className="text-sm text-emerald-400 mt-1.5" data-testid="ms-change">Change to return: ₹{Math.max(0, Number(cashReceived) - bill.remaining)}</p>
                    : <p className="text-sm text-amber-400 mt-1.5" data-testid="ms-short">Incomplete — still due ₹{bill.remaining - Number(cashReceived)}</p>)}
                </div>
              )}
            </div>

            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose} className="h-11 border-purple-500/30 bg-transparent"><X size={15} /> Close</Button>
              <Button data-testid="ms-end-bill" disabled={busy} onClick={endBill} className="h-11 flex-1 bg-emerald-600 hover:bg-emerald-500 font-semibold">{busy ? <Loader2 className="animate-spin" /> : <><Printer size={16} /> Settle & Print Bill</>}</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
